import { CompareArrows, Lightbulb } from "@mui/icons-material";
import type React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import styles from "../styles.module.scss";
import type { ChatMessage as ChatMessageType } from "../types";
import KnowledgeCard from "./KnowledgeCard";
import PluginSearchResultsSection from "./PluginSearchResultsSection";
import ToolCallProgress from "./ToolCallProgress";

// ユーティリティ関数
const normalizeNewlines = (input: unknown): string | unknown => {
  if (typeof input !== "string") return input;
  let s = input as string;
  // 一重エスケープされた改行を実改行に
  s = s.replace(/\\r\\n|\\r|\\n/g, "\n");
  // ふつうのエスケープ(\n)も実改行に
  s = s
    .replace(/\\n/g, "\n")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\r/g, "\n");
  // 実改行の表記揺れを統一
  s = s.replace(/\r\n|\r|\n/g, "\n");
  return s;
};

const toMarkdownHardBreaks = (input: string): string => {
  return input.replace(/\r\n|\r|\n/g, "  \n");
};

const extractKnowledgeMessage = (
  content: any,
): { title: string; metaPrompt: string } | undefined => {
  // contentが文字列でない場合はundefinedを返す
  if (typeof content !== "string") {
    return undefined;
  }

  const lines = content.split("\n");

  if (
    lines.length >= 2 &&
    lines[0]?.startsWith("タイトル:") &&
    lines[1]?.startsWith("メタプロンプト:")
  ) {
    const title = lines[0]?.replace("タイトル:", "").trim() || "";
    const metaPrompt = lines[1]?.replace("メタプロンプト:", "").trim() || "";
    return { title, metaPrompt };
  }
  return undefined;
};

// Markdownコンポーネントの共通設定
const markdownComponents = {
  h1: ({ children }: any) => <h1 className={styles["markdownH1"]}>{children}</h1>,
  h2: ({ children }: any) => <h2 className={styles["markdownH2"]}>{children}</h2>,
  h3: ({ children }: any) => <h3 className={styles["markdownH3"]}>{children}</h3>,
  p: ({ children }: any) => <p className={styles["markdownP"]}>{children}</p>,
  ul: ({ children }: any) => <ul className={styles["markdownUl"]}>{children}</ul>,
  ol: ({ children }: any) => <ol className={styles["markdownOl"]}>{children}</ol>,
  li: ({ children }: any) => <li className={styles["markdownLi"]}>{children}</li>,
  code: ({ children, className, ...props }: any) => {
    const isInline = !className || !className.includes("language-");
    return isInline ? (
      <code className={styles["markdownInlineCode"]} {...props}>
        {children}
      </code>
    ) : (
      <pre className={styles["markdownCodeBlock"]}>
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    );
  },
  blockquote: ({ children }: any) => (
    <blockquote className={styles["markdownBlockquote"]}>{children}</blockquote>
  ),
  table: ({ children }: any) => (
    <div className={styles["markdownTableWrapper"]}>
      <table className={styles["markdownTable"]}>{children}</table>
    </div>
  ),
  th: ({ children }: any) => <th className={styles["markdownTh"]}>{children}</th>,
  td: ({ children }: any) => <td className={styles["markdownTd"]}>{children}</td>,
  strong: ({ children }: any) => <strong className={styles["markdownStrong"]}>{children}</strong>,
  em: ({ children }: any) => <em className={styles["markdownEm"]}>{children}</em>,
  a: ({ children, href }: any) => (
    <a href={href} className={styles["markdownLink"]} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

// アシスタント用のMarkdownコンポーネント（追加設定あり）
const assistantMarkdownComponents = {
  ...markdownComponents,
  pre: ({ children }: any) => <pre className={styles["markdownPre"]}>{children}</pre>,
  thead: ({ children }: any) => <thead className={styles["markdownThead"]}>{children}</thead>,
  tbody: ({ children }: any) => <tbody className={styles["markdownTbody"]}>{children}</tbody>,
  tr: ({ children }: any) => <tr className={styles["markdownTr"]}>{children}</tr>,
};

// メッセージタイプ判定
const getMessageType = (
  message: ChatMessageType,
  knowledgeInfo: { title: string; metaPrompt: string } | undefined,
) => {
  const isAssistant = message.sender === "assistant";
  const isSystem = message.sender === "system";
  const isKnowledge = knowledgeInfo !== undefined;
  const isUser = !isAssistant && !isSystem;

  return { isAssistant, isSystem, isKnowledge, isUser };
};

interface ChatMessageProps {
  projectId: string;
  message: ChatMessageType;
  onPreviewClick?: () => void;
  streamingContent?: string;
  isStreaming?: boolean;
  originalDocument?: string;
}

// サブコンポーネント: ユーザーメッセージ
const UserMessage: React.FC<{ content: string }> = ({ content }) => (
  <div className={styles["userBubble"]}>
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
      {typeof content === "string" ? (normalizeNewlines(content) as string) : ""}
    </ReactMarkdown>
  </div>
);

// サブコンポーネント: システムメッセージ
const SystemMessage: React.FC<{ content: string }> = ({ content }) => (
  <div className={styles["systemBubble"]}>
    <p style={{ whiteSpace: "pre-wrap" }}>{normalizeNewlines(content) as string}</p>
  </div>
);

// サブコンポーネント: ナレッジカード
const KnowledgeMessage: React.FC<{
  projectId: string;
  message: ChatMessageType;
  knowledgeInfo: { title: string; metaPrompt: string };
}> = ({ projectId, message, knowledgeInfo }) => (
  <KnowledgeCard
    projectId={projectId}
    knowledgeId={message.metadata?.knowledgeId || ""}
    title={knowledgeInfo.title || ""}
    metaPrompt={knowledgeInfo.metaPrompt || ""}
    timestamp={message.timestamp}
  />
);

// サブコンポーネント: ツールコール進捗
const ToolCallSection: React.FC<{
  message: ChatMessageType;
  isStreaming: boolean;
}> = ({ message, isStreaming }) => {
  if (!message.toolCalls || message.toolCalls.length === 0) return null;

  return (
    <div
      className={`${styles["thinkingSection"]} ${isStreaming ? styles["thinkingSectionStreaming"] : ""}`}
    >
      <div className={styles["thinkingTitle"]}>{isStreaming ? "REQXIGNが考え中" : "実行履歴"}</div>
      <div className={styles["toolCallsContainer"]}>
        {message.toolCalls.map((toolCall, index) => (
          <ToolCallProgress key={`${toolCall.toolCallId}-${index}`} toolCall={toolCall} />
        ))}
      </div>
    </div>
  );
};

// サブコンポーネント: アシスタントテキスト
const AssistantText: React.FC<{
  message: ChatMessageType;
  streamingContent?: string | undefined;
  isStreaming?: boolean | undefined;
}> = ({ message, streamingContent, isStreaming }) => {
  const getContent = () => {
    // ストリーミング中は streamingContent を最優先
    if (isStreaming && typeof streamingContent === "string" && streamingContent.length >= 0) {
      return toMarkdownHardBreaks(normalizeNewlines(streamingContent) as string);
    }

    // 構造化データがある場合は、その message フィールドを表示
    if (message.structuredData?.["message"]) {
      return toMarkdownHardBreaks(normalizeNewlines(message.structuredData["message"]) as string);
    }

    // オブジェクトが文字列として表示されるのを防ぐ
    if (typeof message.content === "function") {
      return "";
    }

    if (typeof message.content === "object" && message.content !== null) {
      const contentObj = message.content as any;
      if (contentObj.message && typeof contentObj.message === "string") {
        return toMarkdownHardBreaks(normalizeNewlines(contentObj.message) as string);
      }
      return toMarkdownHardBreaks(
        normalizeNewlines(JSON.stringify(message.content, null, 2)) as string,
      );
    }

    const content = message.content;

    if (typeof content === "string") {
      const cleaned = content.includes("[object Object]")
        ? content.replace(/\[object Object\]/g, "")
        : content;
      return toMarkdownHardBreaks(normalizeNewlines(cleaned) as string);
    }

    return "";
  };

  return (
    <div className={styles["assistantText"]}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={assistantMarkdownComponents}
      >
        {getContent()}
      </ReactMarkdown>
    </div>
  );
};

// サブコンポーネント: 参考URL（構造化sourceUrlsフィールドからレンダリング）
const SourceUrlsSection: React.FC<{
  sourceUrls: Array<{ title: string; url: string }>;
}> = ({ sourceUrls }) => (
  <div className={styles["sourceUrlsSection"]}>
    <h4 className={styles["sourceUrlsTitle"]}>参考URL</h4>
    <ul className={styles["sourceUrlsList"]}>
      {sourceUrls.map((source) => (
        <li key={source.url} className={styles["sourceUrlsItem"]}>
          <a
            href={source.url}
            className={styles["sourceUrlsLink"]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {source.title}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

// サブコンポーネント: 更新コンテンツ
const UpdateContent: React.FC<{
  onPreviewClick?: (() => void) | undefined;
}> = ({ onPreviewClick }) => (
  <div className={styles["updateContentSection"]}>
    <div className={styles["updateContentHeader"]}>
      <div className={styles["updateInfo"]}>
        <CompareArrows className={styles["updateIcon"] || ""} />
        <span className={styles["updateTitle"]}>ドキュメント更新内容</span>
      </div>
    </div>
    <div className={styles["documentActions"]}>
      <button className={styles["previewLink"]} onClick={() => onPreviewClick?.()}>
        差分をプレビュー
      </button>
    </div>
  </div>
);

const ChatMessage: React.FC<ChatMessageProps> = ({
  projectId,
  message,
  onPreviewClick,
  streamingContent,
  isStreaming,
  originalDocument,
}) => {
  // ナレッジメッセージかどうかを判定
  const knowledgeInfo = extractKnowledgeMessage(message.content);
  const { isAssistant, isSystem, isKnowledge, isUser } = getMessageType(message, knowledgeInfo);

  return (
    <div
      className={`${styles["messageWrapper"]} ${
        isAssistant || isKnowledge
          ? styles["assistantMessage"]
          : isSystem
            ? styles["systemMessage"]
            : styles["userMessage"]
      }`}
      data-testid="chat-message"
    >
      {/* アバター表示 */}
      {(isAssistant || isKnowledge) && (
        <div className={styles["assistantAvatar"]}>
          <img src="/reqxignlogo.ico" alt="REQXIGN AI" />
        </div>
      )}
      {isSystem && !isKnowledge && (
        <div className={styles["systemAvatar"]}>
          <Lightbulb />
        </div>
      )}

      <div className={`${styles["messageContent"]} ${isUser ? styles["userMessageContent"] : ""}`}>
        {/* ユーザーメッセージ */}
        {isUser && <UserMessage content={message.content} />}

        {/* ナレッジメッセージ */}
        {isKnowledge && knowledgeInfo && (
          <KnowledgeMessage projectId={projectId} message={message} knowledgeInfo={knowledgeInfo} />
        )}

        {/* システムメッセージ */}
        {isSystem && !isKnowledge && <SystemMessage content={message.content} />}

        {/* アシスタントメッセージ */}
        {isAssistant && !isKnowledge && (
          <>
            {/* ツールコール進捗 */}
            <ToolCallSection message={message} isStreaming={isStreaming || false} />

            {/* アシスタントテキスト */}
            <AssistantText
              message={message}
              streamingContent={streamingContent}
              isStreaming={isStreaming}
            />

            {/* プラグイン検索結果（回答本文の補足） */}
            {message.pluginResults && (
              <PluginSearchResultsSection pluginResults={message.pluginResults} />
            )}

            {/* 参考URL */}
            {message.sourceUrls && message.sourceUrls.length > 0 && (
              <SourceUrlsSection sourceUrls={message.sourceUrls} />
            )}

            {/* 更新コンテンツ */}
            {message.updatedContent &&
              !message.isError &&
              message.updatedContent !== originalDocument && (
                <UpdateContent onPreviewClick={onPreviewClick} />
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
